import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation'; 
import { useSettings } from '../contexts/SettingsContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { useModuleAccess } from '../contexts/ModuleAccessContext';
import { useCompanyPlan } from '../hooks/useCompanyPlan';
import { supabase } from '../lib/supabase';
import CompanyHeader from './CompanyHeader';
import Tooltip from './Sidebar/Tooltip';
import { 
  Clock, 
  BarChart2,
  MessageCircle,
  Users,
  Activity,
  Link,
  FolderKanban,
  Calendar,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  ListTodo, 
  ShoppingCart,
  Package,
  ClipboardList,
  PanelLeftClose,
  PanelLeft,
  Building2,
  Landmark,
  ArrowLeftRight,
  LayoutDashboard,
  Calculator,
  Menu,
  X,
  DollarSign,
  TrendingUp,
  FileText,
  GitCompare,
  Settings,
  GanttChart,
  Briefcase,
  CreditCard,
  Wrench
} from 'lucide-react';
import type { TranslationKey } from '../translations';
import { clickableProps } from '../utils/a11y';

// Mantemos a interface original para compatibilidade
interface Project {
  id: string | number;
  name: string;
  type?: 'design' | 'development' | 'marketing' | 'archived';
  status?: 'active' | 'archived' | 'completed' | 'pending';
}

interface NavItem {
  icon: React.ElementType;
  label: TranslationKey | string;
  id: string;
  submenu?: { id: string; label: TranslationKey | string; icon: React.ElementType }[];
}

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  projects: Project[];
}

const timeSubmenu = [
  { id: 'tracker', label: 'Rastreador', icon: Clock },
  { id: 'calendar', label: 'Calendário', icon: Calendar },
  { id: 'reports', label: 'Relatórios', icon: BarChart2 }
];

const tools: NavItem[] = [
  { icon: BarChart2, label: 'nankinInsights', id: 'nankin-insights' },
  {
    icon: FolderKanban,
    label: 'projects',
    id: 'projects',
    submenu: [
      { id: 'price-simulator', label: 'Simulador de propostas', icon: Calculator }
    ]
  },
  { 
    icon: ClipboardList, 
    label: 'myActivities', 
    id: 'my-activities',
    submenu: [
      { id: 'activities', label: 'activities', icon: ClipboardList },
      { id: 'activities-calendar', label: 'calendar', icon: Calendar },
      { id: 'activities-strategic', label: 'Visão Gantt', icon: GanttChart },
      { id: 'tasks-unified', label: 'minhasNotas', icon: ListTodo }
    ]
  },
  { icon: Clock, label: 'timeUsage', id: 'time' },
  {
    icon: Package,
    label: 'productSpec',
    id: 'product-spec',
    submenu: [
      { id: 'product-spec-products', label: 'Produtos', icon: Package },
      { id: 'product-spec-services', label: 'Serviços', icon: Wrench },
      { id: 'product-spec-budget', label: 'Orçamento', icon: DollarSign }
    ]
  },
];

// Nova seção de Gestão Operacional
const operationalTools: NavItem[] = [
  { 
    icon: DollarSign, 
    label: 'Finanças', 
    id: 'finances',
    submenu: [
      { id: 'financial-dashboard', label: 'Dashboard financeiro', icon: BarChart2 },
      { id: 'financial-movements', label: 'Movimentações', icon: ArrowLeftRight },
      { id: 'reconciliation', label: 'reconciliation', icon: GitCompare },
      { id: 'financial-projects', label: 'Projetos', icon: FolderKanban },
      { id: 'financial-calendar', label: 'Agenda Financeira', icon: Calendar },
      { id: 'financial-reports', label: 'Relatórios', icon: FileText },
      { id: 'financial-registers', label: 'Cadastros', icon: CreditCard }
    ]
  },
  { 
    icon: Users, 
    label: 'Comercial', 
    id: 'commercial',
    submenu: [
      { id: 'sales-pipeline', label: 'Pipeline de Vendas', icon: TrendingUp },
      { id: 'clients', label: 'Clientes', icon: Users }
    ]
  },
  {
    icon: Landmark,
    label: 'Controles',
    id: 'controls',
    submenu: [
      { id: 'bank-accounts', label: 'Contas Bancárias', icon: Landmark }
    ]
  }
];

const Sidebar: React.FC<SidebarProps> = ({ activeSection, onSectionChange, projects }) => {
  const { t } = useTranslation();
  const { showSubmenus } = useSettings();
  const { organizationId } = useOrganization();
  const { canAccessOperacional } = useCompanyPlan();
  const { canAccess } = useModuleAccess();
  const visibleItems = (items: NavItem[]) =>
    items
      .filter((item) => canAccess(item.id))
      .map((item) => (item.submenu ? { ...item, submenu: item.submenu.filter((sub) => canAccess(sub.id)) } : item));
  const visibleTools = visibleItems(tools);
  const visibleOperational = visibleItems(operationalTools);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState('');
  const [isProjectsOpen, setIsProjectsOpen] = useState(showSubmenus);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Recuperar estado do localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [isTimeOpen, setIsTimeOpen] = useState(showSubmenus);
  const [isActivitiesOpen, setIsActivitiesOpen] = useState(showSubmenus);
  const [isProductSpecOpen, setIsProductSpecOpen] = useState(showSubmenus);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Estados para Gestão Operacional
  const [isFinancesOpen, setIsFinancesOpen] = useState(showSubmenus);
  const [isCommercialOpen, setIsCommercialOpen] = useState(showSubmenus);
  const [isControlsOpen, setIsControlsOpen] = useState(showSubmenus);

  // Salvar estado de colapsado no localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  // Logo e nome numa consulta só; o CompanyHeader recebe os dois por prop.
  useEffect(() => {
    const fetchCompany = async () => {
      if (organizationId) {
        try {
          const { data, error } = await supabase
            .from('organizations')
            .select('name, logo_url')
            .eq('id', organizationId)
            .single<{ name: string | null; logo_url: string | null }>();

          if (error) throw error;
          setLogoUrl(data?.logo_url || null);
          setOrganizationName(data?.name || '');
        } catch (err) {
          console.error('Error fetching company:', err);
        }
      }
    };

    fetchCompany();

    window.addEventListener('companyProfileUpdated', fetchCompany);
    return () => window.removeEventListener('companyProfileUpdated', fetchCompany);
  }, [organizationId]);

  useEffect(() => {
    setIsProjectsOpen(showSubmenus);
    setIsTimeOpen(showSubmenus);
    setIsActivitiesOpen(showSubmenus);
    setIsProductSpecOpen(showSubmenus);
    setIsFinancesOpen(showSubmenus);
    setIsCommercialOpen(showSubmenus);
    setIsControlsOpen(showSubmenus);
  }, [showSubmenus]);

  // Fecha sidebar ao navegar (mobile)
  useEffect(() => {
    if (sidebarOpen) setSidebarOpen(false);
    // eslint-disable-next-line
  }, [activeSection]);

  const handleTimeClick = () => {
    onSectionChange('time');
    if (!showSubmenus) {
      return;
    }
    setIsTimeOpen(prev => !prev);
  };

  // Removido: handleProjectClick não é mais usado

  // Função adaptativa para verificar se um projeto está arquivado
  const isProjectArchived = (project: Project): boolean => {
    // Verificar se o projeto tem a propriedade status (nova estrutura)
    if (project.status !== undefined) {
      return project.status === 'archived';
    }
    // Verificar se o projeto tem a propriedade type (estrutura antiga)
    if (project.type !== undefined) {
      return project.type === 'archived';
    }
    // Se não tiver nenhuma das propriedades, assumir que não está arquivado
    return false;
  };

  // Filter out archived projects from navigation - versão adaptativa
  const activeProjects = projects ? projects.filter(project => !isProjectArchived(project)) : [];

  // Função segura para comparar IDs, considerando possíveis diferenças de tipo
  const isSameId = (id1: string | number | undefined, id2: string | number | undefined): boolean => {
    if (id1 === undefined || id2 === undefined) return false;
    // Converter para string para comparação segura
    return String(id1) === String(id2);
  };

  const isActiveSection = (section: string) => {
    if (section === activeSection) return true;
    if (section === 'time' && timeSubmenu.some(item => item.id === activeSection)) {
      return true;
    }
    if (section === 'my-activities') {
      return activeSection === 'my-activities' || 
             activeSection === 'activities' ||
             activeSection === 'activities-monitoring' ||
             activeSection === 'activities-calendar' ||
             activeSection === 'activities-strategic' ||
             activeSection === 'tasks-unified';
    }
    if (section === 'product-spec') {
      return [
        'product-spec',
        'product-spec-products',
        'product-spec-services',
        'product-spec-budget'
      ].includes(activeSection);
    }
    if (section === 'projects') {
      return activeSection === 'projects' || activeSection === 'price-simulator';
    }
    // Seções operacionais
    if (section === 'finances') {
      return ['financial-dashboard', 'financial-movements', 'reconciliation', 'financial-projects', 'financial-calendar', 'financial-reports', 'financial-registers'].includes(activeSection);
    }
    if (section === 'commercial') {
      return ['clients', 'sales-pipeline'].includes(activeSection);
    }
    if (section === 'controls') {
      return ['bank-accounts', 'help'].includes(activeSection);
    }
    return false;
  };

  const getItemClasses = (section: string) => {
    const isActive = isActiveSection(section);
    return `flex items-center px-4 py-3 text-base rounded-lg cursor-pointer transition-all ${
      isActive
        ? 'text-salmon-muted bg-salmon-bg-soft font-medium'
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
    }`;
  };

  // Removido: getIconClasses não é mais usado

  return (
    <>
      {/* Botão hambúrguer mobile */}
      <button
        className="
          md:hidden fixed top-4 left-4 z-[101]
          bg-white border border-gray-200 rounded-full p-3
          shadow-[var(--shadow-2)] transition
          hover:scale-105 hover:bg-salmon-light active:scale-95
          focus:outline-none focus:ring-2 focus:ring-salmon
        "
        onClick={() => setSidebarOpen(true)}
        aria-label="Abrir menu"
      >
        <PanelLeft className="w-7 h-7 text-salmon" />
      </button>

      {/* Overlay escuro */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-[100] md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={
          `
          fixed top-0 left-0 h-full z-[110] bg-white border-r transition-all duration-300
          ${isCollapsed ? 'w-20' : 'w-72 md:w-80'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:sticky md:top-0 md:h-screen
          flex flex-col
          `
        }
        style={{ minWidth: isCollapsed ? '5rem' : '16rem', maxWidth: isCollapsed ? '5rem' : '20rem' }}
      >
        <div className="flex-shrink-0 p-3">
          <CompanyHeader 
            isCollapsed={isCollapsed} 
            logoUrl={logoUrl}
            organizationName={organizationName}
            setActiveSection={onSectionChange}
          />
        </div>
        
        {/* Botão de colapsar/expandir - design limpo e moderno */}
        <div className={`hidden md:flex ${isCollapsed ? 'justify-center px-2' : 'justify-end px-3'} mb-1`}>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="relative p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 group"
            aria-label={isCollapsed ? "Expandir menu" : "Recolher menu"}
            title={isCollapsed ? "Expandir Menu" : "Recolher Menu"}
          >
            {isCollapsed ? (
              <>
                <ChevronsRight className="w-5 h-5" />
              </>
            ) : (
              <ChevronsLeft className="w-5 h-5" />
            )}
          </button>
        </div>
        
        {/* Linha divisória sutil */}
        <div className="mx-3 mb-1 border-t border-gray-100"></div>

        <nav className={`flex-1 ${isCollapsed ? 'overflow-hidden px-2' : 'overflow-y-auto custom-scrollbar px-4'} pb-4`}>
          {/* Tools Section */}
          <div className="py-1">
            {!isCollapsed && (
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase mb-2 tracking-wide">GESTÃO DE PROJETOS</p>
            )}
            <div className="space-y-1">
              {visibleTools.map((item) => (
                <div key={item.id}>
                  {item.submenu ? (
                    <div>
                      <div
                        className={`${getItemClasses(item.id)} relative group ${
                          isCollapsed ? 'px-0 justify-center' : 'justify-between'
                        }`}
                      >
                        <Tooltip 
                          content={typeof item.label === 'string' ? t(item.label as TranslationKey) : item.label}
                          isVisible={isCollapsed}
                        >
                          <div {...clickableProps} 
                            className={`flex items-center ${isCollapsed ? 'justify-center' : 'flex-1 cursor-pointer'} relative`}
                            onClick={() => {
                              if (item.id === 'projects') {
                                onSectionChange('projects' as any);
                              } else if (item.id === 'my-activities') {
                                onSectionChange('my-activities' as any);
                              } else if (item.id === 'product-spec') {
                                onSectionChange('product-spec' as any);
                              } else {
                                onSectionChange(item.id as any);
                              }
                            }}
                          >
                            <item.icon className={`w-5 h-5 ${isCollapsed ? 'm-0' : 'mr-3'} ${
                              isActiveSection(item.id) ? 'text-salmon-muted' : 'text-gray-500'
                            }`} />
                            {!isCollapsed && (typeof item.label === 'string' ? t(item.label as TranslationKey) : item.label)}
                          </div>
                        </Tooltip>
                        {!isCollapsed && (item.id === 'projects' || item.id === 'my-activities' || item.id === 'product-spec') && (
                          <button
                            aria-label="Mostrar ou ocultar submenu"
                            className="p-1 rounded hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.id === 'projects') {
                                if (showSubmenus) setIsProjectsOpen(prev => !prev);
                              } else if (item.id === 'my-activities') {
                                if (showSubmenus) setIsActivitiesOpen(prev => !prev);
                              } else if (item.id === 'product-spec') {
                                if (showSubmenus) setIsProductSpecOpen(prev => !prev);
                              }
                            }}
                          >
                            <ChevronRight 
                              className={`w-4 h-4 transition-transform ${
                                item.id === 'projects' ?
                                  (showSubmenus && isProjectsOpen ? 'rotate-90' : '') :
                                item.id === 'my-activities' ?
                                  (showSubmenus && isActivitiesOpen ? 'rotate-90' : '') :
                                item.id === 'product-spec' ?
                                  (showSubmenus && isProductSpecOpen ? 'rotate-90' : '') : ''
                              }`}
                            />
                          </button>
                        )}
                      </div>
                      {(item.id === 'my-activities' && showSubmenus && isActivitiesOpen && item.submenu) && !isCollapsed && (
                        <div className="space-y-0.5 ml-5 pl-4 border-l-2 border-gray-200">
                          {item.submenu.map((submenu) => (
                            <button
                              key={submenu.id}
                              className={`w-full flex items-center px-4 py-2.5 text-base rounded-lg cursor-pointer ml-0 transition-all ${
                                activeSection === submenu.id
                                  ? 'text-salmon-muted bg-salmon-bg-soft'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSectionChange(submenu.id);
                              }}
                            >
                              <submenu.icon className={`w-4 h-4 mr-3 ${
                                activeSection === submenu.id
                                  ? 'text-salmon-muted'
                                  : 'text-gray-400'
                              }`} />
                              {submenu.id === 'overdue' ? 'Atrasadas' : 
                               submenu.id === 'new-task' ? 'Entrada' : 
                               submenu.id === 'today' ? 'Hoje' : 
                               submenu.id === 'upcoming' ? 'Próximos dias' : 
                               submenu.id === 'activities-strategic' ? 'Visão Gantt' :
                                (typeof submenu.label === 'string' ? t(submenu.label as TranslationKey) : submenu.label)}
                            </button>
                          ))}
                        </div>
                      )}
                      {((item.id === 'product-spec' && isProductSpecOpen) || (item.id === 'projects' && isProjectsOpen)) && showSubmenus && item.submenu && !isCollapsed && (
                        <div className="space-y-0.5 ml-5 pl-4 border-l-2 border-gray-200">
                          {item.submenu.map((submenu) => (
                            <button
                              key={submenu.id}
                              className={`w-full flex items-center px-4 py-2.5 text-base rounded-lg cursor-pointer ml-0 transition-all ${
                                activeSection === submenu.id
                                  ? 'text-salmon-muted bg-salmon-bg-soft'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSectionChange(submenu.id);
                              }}
                            >
                              <submenu.icon className={`w-4 h-4 mr-3 ${
                                activeSection === submenu.id
                                  ? 'text-salmon-muted'
                                  : 'text-gray-400'
                              }`} />
                              {typeof submenu.label === 'string' ? submenu.label : submenu.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div {...clickableProps}
                        className={`${getItemClasses(item.id)} relative group ${
                          isCollapsed ? 'px-0 justify-center' : 'justify-between'
                        }`}
                        onClick={() => {
                          if (item.id === 'time') {
                            handleTimeClick();
                          } else {
                            onSectionChange(item.id);
                          }
                        }}
                      >
                        <Tooltip 
                          content={typeof item.label === 'string' ? t(item.label as TranslationKey) : item.label}
                          isVisible={isCollapsed}
                        >
                          <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''} relative`}>
                            <item.icon className={`w-5 h-5 ${isCollapsed ? 'm-0' : 'mr-3'} ${
                              isActiveSection(item.id) ? 'text-salmon-muted' : 'text-gray-500'
                            }`} />
                            {!isCollapsed && (typeof item.label === 'string' ? t(item.label as TranslationKey) : item.label)}
                          </div>
                        </Tooltip>
                        {!isCollapsed && item.id === 'time' && (
                          <ChevronRight 
                            className={`w-4 h-4 transition-transform ${isTimeOpen ? 'rotate-90' : ''}`}
                          />
                        )}
                      </div>
                      {showSubmenus && item.id === 'time' && isTimeOpen && !isCollapsed && (
                        <div className="space-y-0.5 ml-5 pl-4 border-l-2 border-gray-200">
                          {timeSubmenu.map((submenu) => (
                            <button
                              key={submenu.id}
                              className={`w-full flex items-center px-4 py-2.5 text-base rounded-lg cursor-pointer ml-0 transition-all ${
                                activeSection === submenu.id
                                  ? 'text-salmon-muted bg-salmon-bg-soft'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onSectionChange(submenu.id);
                              }}
                            >
                              <submenu.icon className={`w-4 h-4 mr-3 ${
                                activeSection === submenu.id
                                  ? 'text-salmon-muted'
                                  : 'text-gray-400'
                              }`} />
                              {submenu.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Operational Management Section - Only show if user has access */}
          {canAccessOperacional && visibleOperational.length > 0 && (
            <div className="py-1 mt-3 pt-3 border-t border-gray-100">
              {!isCollapsed && (
                <p className="px-4 text-xs font-semibold text-gray-400 uppercase mb-2 tracking-wide">GESTÃO OPERACIONAL</p>
              )}
              <div className="space-y-1">
                {visibleOperational.map((item) => (
                <div key={item.id}>
                  <div>
                    <div {...clickableProps}
                      className={`${getItemClasses(item.id)} relative group ${
                        isCollapsed ? 'px-0 justify-center' : 'justify-between'
                      }`}
                      onClick={() => {
                        if (item.id === 'finances') {
                          onSectionChange('financial-dashboard');
                          if (showSubmenus) setIsFinancesOpen(prev => !prev);
                        } else if (item.id === 'commercial') {
                          onSectionChange('sales-pipeline');
                          if (showSubmenus) setIsCommercialOpen(prev => !prev);
                        } else if (item.id === 'controls') {
                          onSectionChange('bank-accounts');
                          if (showSubmenus) setIsControlsOpen(prev => !prev);
                        } else {
                          onSectionChange(item.id);
                        }
                      }}
                    >
                      <Tooltip 
                        content={item.label}
                        isVisible={isCollapsed}
                      >
                        <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''} relative`}>
                          <item.icon className={`w-4 h-4 ${isCollapsed ? 'm-0' : 'mr-3'} ${
                            isActiveSection(item.id) ? 'text-salmon-muted' : 'text-gray-400'
                          }`} />
                          {!isCollapsed && item.label}
                        </div>
                      </Tooltip>
                      {!isCollapsed && item.submenu && (
                        <ChevronRight 
                          className={`w-4 h-4 transition-transform ${
                            item.id === 'finances' ? 
                              (showSubmenus && isFinancesOpen ? 'rotate-90' : '') :
                            item.id === 'commercial' ?
                              (showSubmenus && isCommercialOpen ? 'rotate-90' : '') :
                              (showSubmenus && isControlsOpen ? 'rotate-90' : '')
                          }`}
                        />
                      )}
                    </div>
                    {item.submenu && showSubmenus && !isCollapsed && (
                      <>
                        {item.id === 'finances' && isFinancesOpen && (
                          <div className="space-y-0.5 ml-5 pl-4 border-l-2 border-gray-200">
                            {item.submenu.map((submenu) => (
                              <button
                                key={submenu.id}
                                className={`w-full flex items-center px-4 py-2.5 text-base rounded-lg cursor-pointer ml-0 transition-all ${
                                  activeSection === submenu.id
                                    ? 'text-salmon-muted bg-salmon-bg-soft'
                                    : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSectionChange(submenu.id);
                                }}
                              >
                                <submenu.icon className={`w-4 h-4 mr-3 ${
                                  activeSection === submenu.id
                                    ? 'text-salmon-muted'
                                    : 'text-gray-400'
                                }`} />
                                {submenu.id === 'reconciliation' 
                                  ? t(submenu.label as TranslationKey) 
                                  : (typeof submenu.label === 'string' ? submenu.label : submenu.label)}
                              </button>
                            ))}
                          </div>
                        )}
                        {item.id === 'commercial' && isCommercialOpen && (
                          <div className="space-y-0.5 ml-5 pl-4 border-l-2 border-gray-200">
                            {item.submenu.map((submenu) => (
                              <button
                                key={submenu.id}
                                className={`w-full flex items-center px-4 py-2.5 text-base rounded-lg cursor-pointer ml-0 transition-all ${
                                  activeSection === submenu.id
                                    ? 'text-salmon-muted bg-salmon-bg-soft'
                                    : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSectionChange(submenu.id);
                                }}
                              >
                                <submenu.icon className={`w-4 h-4 mr-3 ${
                                  activeSection === submenu.id
                                    ? 'text-salmon-muted'
                                    : 'text-gray-400'
                                }`} />
                                {submenu.label}
                              </button>
                            ))}
                          </div>
                        )}
                        {item.id === 'controls' && isControlsOpen && (
                          <div className="space-y-0.5 ml-5 pl-4 border-l-2 border-gray-200">
                            {item.submenu.map((submenu) => (
                              <button
                                key={submenu.id}
                                className={`w-full flex items-center px-4 py-2.5 text-base rounded-lg cursor-pointer ml-0 transition-all ${
                                  activeSection === submenu.id
                                    ? 'text-salmon-muted bg-salmon-bg-soft'
                                    : 'text-gray-600 hover:bg-gray-100'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSectionChange(submenu.id);
                                }}
                              >
                                <submenu.icon className={`w-4 h-4 mr-3 ${
                                  activeSection === submenu.id
                                    ? 'text-salmon-muted'
                                    : 'text-gray-400'
                                }`} />
                                {submenu.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

        </nav>

        {/* Footer Menu */}
      </div>
    </>
  );
};

export default Sidebar;